import React from "react";
import SiteNav from "@/components/SiteNav";
import Footer from "@/components/Footer";
import { ProcessSteps, FounderPilot } from "@/components/marketing";

export default function AiLabs() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F6F3EC]">
      <SiteNav />

      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-24 pb-20 sm:pt-32 sm:pb-24">
          <p className="eyebrow text-[#C79447] reveal">For AI labs</p>
          <h1 className="mt-6 font-display text-4xl sm:text-6xl font-semibold tracking-tight leading-[1.05] max-w-4xl reveal reveal-1">Specialised human judgment for better AI.</h1>
          <p className="mt-8 text-lg sm:text-xl text-[#D4D0C6] leading-relaxed max-w-3xl reveal reveal-2">
            AI systems are becoming more capable. Evaluating them increasingly requires people who understand the subject matter behind the task. FIAD is a structured human capability layer for that work.
          </p>
        </div>
      </section>

      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-28 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="eyebrow text-[#71717A]">The problem</p>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl font-medium tracking-tight leading-[1.12]">Generic pipelines miss domain depth.</h2>
          </div>
          <div className="lg:col-span-7 lg:pt-12 space-y-5 text-lg text-[#A1A1AA] leading-relaxed">
            <p>AI evaluation increasingly spans language, law, healthcare, coding and other specialised domains.</p>
            <p>Generic evaluation pipelines may not provide the depth of domain understanding required for certain tasks — where a subtle mistake is only visible to someone who knows the field.</p>
          </div>
        </div>
      </section>

      <section className="border-b border-white/10 bg-[#0C0C0C]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-28">
          <p className="eyebrow text-[#C79447]">The FIAD layer</p>
          <h2 className="mt-5 font-display text-3xl sm:text-4xl font-medium tracking-tight mb-12">A structured pipeline, end to end.</h2>
          <ProcessSteps dark />
        </div>
      </section>

      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-28">
          <p className="eyebrow text-[#71717A]">Designed outcomes</p>
          <h2 className="mt-5 font-display text-3xl sm:text-4xl font-medium tracking-tight max-w-2xl">What FIAD is designed to enable.</h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-white/10">
            {[
              "Better-aligned expertise for the task at hand",
              "Faster evaluator readiness through structured training",
              "More structured, explainable human input",
              "Reduced reliance on generic evaluation pools",
              "A clearer path from expertise to evaluation work",
              "Domain depth across languages, law, healthcare and coding",
            ].map((x, i) => (
              <div key={i} className="border-b border-r border-white/10 p-8 min-h-[160px] flex flex-col justify-between">
                <span className="font-mono-num text-2xl text-white/15">0{i + 1}</span>
                <p className="text-[#D4D0C6] leading-relaxed">{x}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-sm text-[#71717A] max-w-2xl">FIAD is an early-stage platform. These are designed outcomes, not guaranteed performance claims.</p>
        </div>
      </section>

      <section>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-28">
          <FounderPilot heading="Let's test it with a pilot." testId="ailabs-founder-pilot" />
        </div>
      </section>

      <Footer />
    </div>
  );
}
